(() => {
  if (customElements.get('product-sticky-cart')) return;

  class ProductStickyCart extends HTMLElement {
    connectedCallback() {
      if (this.bar || this.closest('.cc-popup')) return;
      this.form = document.getElementById(this.dataset.formId);
      this.source = this.form?.querySelector('button[data-add-to-cart-text]');
      this.productForm = this.form?.closest('.product-form');
      if (!this.source || !this.productForm || !('IntersectionObserver' in window)) return;

      // Keep the floating UI outside animated/clipped product columns. The
      // original form remains the source of variant, quantity and bundle state.
      this.bar = document.createElement('div');
      this.bar.className = 'product-sticky-cart';
      this.bar.hidden = true;
      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.className = 'button product-sticky-cart__button';
      this.label = document.createElement('span');
      this.price = document.createElement('span');
      this.price.className = 'product-sticky-cart__price';
      this.button.append(this.label, this.price);
      this.bar.append(this.button);
      document.body.append(this.bar);

      this.button.addEventListener('click', () => {
        if (this.source.disabled) return;
        this.purchasePending = true;
        // Use the existing submit button so all cart and upsell handlers run.
        this.source.click();
        this.sync();
      });

      this.onInvalid = (event) => {
        this.revealForm();
        event.target.focus({ preventScroll: true });
      };
      this.form.addEventListener('invalid', this.onInvalid, true);

      this.sync = () => {
        this.label.textContent = this.source.textContent.trim();
        const currentPrice = this.productForm.querySelector('.price-area .current-price');
        const price = this.source.dataset.pdpPrice || currentPrice?.textContent.trim() || '';
        this.price.textContent = price ? `· ${window.theme && window.theme.ho3Geld ? window.theme.ho3Geld(price) : price}` : '';
        this.price.hidden = !price || this.source.disabled;
        this.button.disabled = this.source.disabled;
        const loading = this.source.classList.contains('btn--in-progress');
        this.button.classList.toggle('btn--in-progress', loading);
        this.button.setAttribute('aria-busy', String(loading));

        const error = this.productForm.querySelector('[data-pdp-upsell-error]');
        if (this.purchasePending && error && !error.hidden && error.textContent.trim()) {
          this.revealForm();
        }
        if (this.schedule) this.schedule();
      };
      this.stateObserver = new MutationObserver(this.sync);
      this.stateObserver.observe(this.productForm, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'class', 'data-pdp-price', 'hidden']
      });

      // The theme places add-to-cart errors beside the original button.
      this.errorObserver = new MutationObserver((records) => {
        if (!this.purchasePending) return;
        const hasError = records.some((record) => Array.from(record.addedNodes).some(
          (node) => node.nodeType === 1 && node.matches('.simple-popup.error')
        ));
        if (hasError) this.revealForm();
      });
      this.errorObserver.observe(document.body, { childList: true });

      // Sichtbarkeit: Leiste nur, wenn der Haupt-Button nicht im Bild ist UND die Leiste
      // den Preisblock (Streichpreis, Preis, Ersparnis) nicht verdeckt. Auf kleinen Handys
      // (360/390) liegt der Preis im ersten Bildschirm genau in der Leistenzone; dort
      // erscheint die Leiste erst, sobald der Preis darüber frei steht.
      this.zone = null;
      this.update = () => {
        this.frame = 0;
        if (!this.bar) return;
        const vh = window.innerHeight;
        const rect = this.source.getBoundingClientRect();
        const outOfView = rect.bottom <= 0 || rect.top >= vh;
        const visible = this.source.getClientRects().length > 0;
        let covers = false;
        // Preisblock jedes Mal frisch suchen: die Varianten-Logik ersetzt ihn beim Laden.
        const priceBlock = this.productForm.querySelector('.price-area');
        if (outOfView && visible && rect.top >= vh && priceBlock) {
          const p = priceBlock.getBoundingClientRect();
          if (p.height > 0) {
            // Leistenzone: zuletzt gemessene Höhe der Leiste, sonst Standard aus dem CSS
            // (mobil 15px Abstand plus 80px Leiste, Desktop 45px plus 80px). Nur die Höhe zählt,
            // weil Preis und Ersparnis nebeneinander stehen und die Leiste mittig darüber liegt.
            const fromBottom = this.zone || (window.innerWidth < 768 ? 95 : 125);
            covers = p.bottom > vh - fromBottom && p.top < vh;
          }
        }
        this.bar.hidden = !outOfView || !visible || covers;
        if (!this.bar.hidden) {
          const b = this.bar.getBoundingClientRect();
          if (b.height > 0) this.zone = vh - b.top;
        }
        if (this.bar.hidden && this.button === document.activeElement) {
          this.source.focus({ preventScroll: true });
        }
      };
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(this.update);
      };
      this.visibilityObserver = new IntersectionObserver(this.schedule, { threshold: 0 });
      this.visibilityObserver.observe(this.source);
      window.addEventListener('scroll', this.schedule, { passive: true });
      this.onResize = () => { this.zone = null; this.schedule(); };
      window.addEventListener('resize', this.onResize, { passive: true });
      // Galerie und Bilder laden nach und verschieben den Preis ohne Scrollen: neu prüfen.
      // Bewertungs-Apps und Bilder kommen teils Sekunden später, deshalb zusätzlich kurz nachprüfen.
      if ('ResizeObserver' in window) {
        this.layoutObserver = new ResizeObserver(this.schedule);
        this.layoutObserver.observe(document.body);
        const section = this.form.closest('.shopify-section');
        if (section) this.layoutObserver.observe(section);
      }
      window.addEventListener('load', this.schedule);
      this.timers = [400, 1200, 2500, 4000, 7000].map((t) => setTimeout(this.schedule, t));
      this.sync();
    }

    revealForm() {
      this.purchasePending = false;
      this.form.scrollIntoView({ block: 'center', behavior: 'auto' });
    }

    disconnectedCallback() {
      this.visibilityObserver?.disconnect();
      window.removeEventListener('scroll', this.schedule);
      window.removeEventListener('resize', this.onResize);
      this.layoutObserver?.disconnect();
      window.removeEventListener('load', this.schedule);
      (this.timers || []).forEach(clearTimeout);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.stateObserver?.disconnect();
      this.errorObserver?.disconnect();
      this.form?.removeEventListener('invalid', this.onInvalid, true);
      this.bar?.remove();
      this.bar = null;
    }
  }

  customElements.define('product-sticky-cart', ProductStickyCart);
})();
